import React, { useCallback, useState } from 'react';
import { BKJButton, BKJIcon, BKJLoadingSpinner, Flex } from '@bookjane2/bookjane-design-library';
import { toastService } from 'services/toastService';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { DASHBOARDS_EXPORT_WORKFORCE_FULFILLMENT } from 'constants/endpoints';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

const MAX_EXPORT_RANGE_DAYS = 90;

function getRangeDays(dateRange: [string, string]): number {
  const start = new Date(dateRange[0]).getTime();
  const end = new Date(dateRange[1]).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
}

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function getExportLabel(isDownloading: boolean, bytesDownloaded: number): string {
  if (!isDownloading) return 'Export CSV';
  if (bytesDownloaded > 0) return `${formatBytes(bytesDownloaded)} downloaded…`;
  return 'Preparing…';
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ExportCsvButton: React.FC = () => {
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const [isDownloading, setIsDownloading] = useState(false);
  const [bytesDownloaded, setBytesDownloaded] = useState(0);

  const isRangeTooLarge = getRangeDays(dateRange) > MAX_EXPORT_RANGE_DAYS;
  const isDisabled = isDownloading || isSelectionCleared(locationIds, positions) || isRangeTooLarge;

  const handleExport = useCallback(() => {
    setIsDownloading(true);
    setBytesDownloaded(0);
    createDashboardsRequest<Blob>({
      url: DASHBOARDS_EXPORT_WORKFORCE_FULFILLMENT(),
      method: 'GET',
      query: buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
      responseType: 'blob',
      onDownloadProgress: (event) => setBytesDownloaded(event.loaded),
    }).then(
      (response) => {
        const fileName = `workforce-fulfillment-${dateRange[0].slice(
          0,
          10,
        )}-to-${dateRange[1].slice(0, 10)}.csv`;
        triggerBrowserDownload(response.data, fileName);
        setIsDownloading(false);
      },
      () => {
        toastService.error('Failed to export CSV. Please try again.');
        setIsDownloading(false);
      },
    );
  }, [dateRange, locationIds, positions, employmentTypes]);

  const label = getExportLabel(isDownloading, bytesDownloaded);

  return (
    <BKJButton
      variant="PurpleSolid"
      width="fit-content"
      disabled={isDisabled}
      onClick={handleExport}
    >
      <Flex alignItems="center" gap="8px">
        {isDownloading ? (
          <BKJLoadingSpinner variant="Small" />
        ) : (
          <BKJIcon iconName="Download" fontSize={16} color="White" />
        )}
        {label}
      </Flex>
    </BKJButton>
  );
};
